// Página PurchaseOrderForm - Formulario para crear/editar pedidos de compra
// Formulario multi-paso para crear pedidos con múltiples proveedores

import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { usePurchaseOrder, usePurchaseOrderOperations } from '../../hooks/usePurchaseOrders'
import { useProducts } from '../../hooks/useFirestore'
import { useSupplies } from '../../hooks/useSupplies'
import { getAllSuppliers } from '../../services/suppliers.service'
import { getCategories } from '../../services/categories.service'
import { getCategoryProfitPercentage } from '../../services/profit.service'
import { useAuth } from '../../hooks/useAuth'
import { useBusinessSettingsContext } from '../../hooks/useBusinessSettingsContext'
import { Timestamp } from 'firebase/firestore'
import Button from '../../components/Button'
import Toast from '../../components/Toast'
import ConfirmDialog from '../../components/ConfirmDialog'
import { formatCurrency, formatCurrencyInput, parseCurrencyInput } from '../../utils/helpers'
import './PurchaseOrderForm.css'

function PurchaseOrderForm() {
  console.log('PurchaseOrderForm: Componente iniciado')
  
  const navigate = useNavigate()
  const { id } = useParams()
  const isEditing = !!id
  
  console.log('PurchaseOrderForm: Params', { id, isEditing })
  
  const { currentUser, userData } = useAuth()
  console.log('PurchaseOrderForm: Auth', { hasUser: !!currentUser, hasUserData: !!userData })
  
  const { activeProfile } = useBusinessSettingsContext()
  const isFashion = (activeProfile?.rubro || '').toLowerCase() === 'indumentaria'
  const getColorLabel = (product, value) => {
    if (!value) return ''
    const colorNames = product?.colorNames && typeof product.colorNames === 'object' ? product.colorNames : null
    const fromMap = colorNames ? colorNames[value] : null
    if (typeof fromMap === 'string' && fromMap.trim()) {
      return fromMap.trim()
    }
    if (product?.color === value && typeof product?.colorName === 'string' && product.colorName.trim()) {
      return product.colorName.trim()
    }
    return value
  }
  
  const { order, loading: loadingOrder } = usePurchaseOrder(id)
  console.log('PurchaseOrderForm: Order hook', { hasOrder: !!order, loadingOrder, orderId: order?.id })
  
  // Verificar si la orden puede editarse (solo si está pendiente o es nueva)
  const canEdit = !isEditing || (order && order.status === 'pending')
  
  const { create, update, loading: saving } = usePurchaseOrderOperations()
  const { products } = useProducts({})
  const { supplies } = useSupplies(true)
  
  console.log('PurchaseOrderForm: Hooks inicializados', { productsCount: products?.length || 0, suppliesCount: supplies?.length || 0 })
  
  const [suppliers, setSuppliers] = useState([])
  const [categories, setCategories] = useState([])
  const [notification, setNotification] = useState(null)
  const [selectedSupplierId, setSelectedSupplierId] = useState('')
  const [showPriceUpdateConfirm, setShowPriceUpdateConfirm] = useState(false)
  const [pendingPriceUpdates, setPendingPriceUpdates] = useState([])
  
  // Estado del formulario
  const [formData, setFormData] = useState({
    suppliers: [], // Array de {supplierId, supplierName, items: []}
    expectedDeliveryDate: '',
    taxes: 0,
    shipping: 0,
    notes: ''
  })
  
  // Estado para agregar items - un objeto por cada proveedor
  const [itemStates, setItemStates] = useState({}) // { supplierId: { itemType, selectedProductId, selectedSupplyId, quantity, price, suggestedPrice, finalPrice, updateProductPrice, currentProductPrice } }
  const [editingFields, setEditingFields] = useState({}) // { supplierId: Set of field names being edited }
  const [editingSummaryFields, setEditingSummaryFields] = useState(new Set())
  
  // Ref para rastrear si ya se cargaron los datos del pedido
  const orderDataLoadedRef = useRef(null) // Guardar el ID del pedido cargado

  const loadInitialData = useCallback(async () => {
    try {
      const [suppliersData, categoriesData] = await Promise.all([
        getAllSuppliers(),
        getCategories()
      ])
      setSuppliers(suppliersData)
      setCategories(categoriesData)
    } catch (error) {
      console.error('Error cargando datos iniciales:', error)
    }
  }, [])

  const loadOrderData = useCallback(() => {
    if (!order) return
    
    try {
      // Convertir expectedDeliveryDate si es Timestamp
      let expectedDeliveryDate = ''
      if (order.expectedDeliveryDate) {
        if (order.expectedDeliveryDate.toDate && typeof order.expectedDeliveryDate.toDate === 'function') {
          expectedDeliveryDate = order.expectedDeliveryDate.toDate().toISOString().split('T')[0]
        } else if (order.expectedDeliveryDate instanceof Date) {
          expectedDeliveryDate = order.expectedDeliveryDate.toISOString().split('T')[0]
        } else {
          expectedDeliveryDate = new Date(order.expectedDeliveryDate).toISOString().split('T')[0]
        }
      }
      
      // Procesar suppliers y completar nombres de categoría si faltan
      const suppliers = (order.suppliers || []).map(supplier => ({
        ...supplier,
        items: (supplier.items || []).map(item => {
          // Si el item no tiene nombre de categoría pero tiene categoryId, buscarlo
          if (!item.category && item.categoryId) {
            const category = categories.find(c => c.id === item.categoryId)
            return {
              ...item,
              category: category ? category.name : 'Sin categoría'
            }
          }
          // Si es un insumo y no tiene categoría, asignar "Insumos"
          if (!item.category && item.type === 'supply') {
            return {
              ...item,
              category: 'Insumos'
            }
          }
          // Si aún no tiene categoría, usar "Sin categoría"
          if (!item.category) {
            return {
              ...item,
              category: 'Sin categoría'
            }
          }
          return item
        })
      }))
      
      setFormData({
        suppliers,
        expectedDeliveryDate,
        taxes: order.taxes || 0,
        shipping: order.shipping || 0,
        notes: order.notes || ''
      })
      
      // Inicializar itemStates para cada proveedor con sus items existentes
      const newItemStates = {}
      suppliers.forEach(supplier => {
        if (supplier && supplier.supplierId) {
          // Si el proveedor tiene items, no inicializamos el estado (se mostrarán en la tabla)
          // Solo inicializamos si no tiene items para permitir agregar nuevos
          if (!supplier.items || supplier.items.length === 0) {
            newItemStates[supplier.supplierId] = {
              itemType: 'product',
              selectedProductId: '',
              selectedSupplyId: '',
              quantity: 1,
              price: '',
              suggestedPrice: null,
              finalPrice: '',
              updateProductPrice: false,
              currentProductPrice: null
            }
          }
        }
      })
      
      if (Object.keys(newItemStates).length > 0) {
        setItemStates(prev => ({ ...prev, ...newItemStates }))
      }
    } catch (error) {
      console.error('Error cargando datos del pedido:', error)
      setNotification({
        type: 'error',
        message: 'Error al cargar los datos del pedido: ' + (error.message || 'Error desconocido')
      })
      setTimeout(() => setNotification(null), 5000)
    }
  }, [order, categories])

  useEffect(() => {
    loadInitialData()
  }, [loadInitialData])

  useEffect(() => {
    // Resetear el ref cuando cambia el ID del pedido
    if (isEditing && id) {
      const currentOrderId = orderDataLoadedRef.current
      if (currentOrderId !== id) {
        orderDataLoadedRef.current = null
      }
    } else if (!isEditing) {
      orderDataLoadedRef.current = null
    }
  }, [id, isEditing])

  useEffect(() => {
    if (isEditing && order && !loadingOrder) {
      if (orderDataLoadedRef.current !== id) {
        console.log('Cargando datos del pedido:', id, 'Order:', order)
        loadOrderData()
        orderDataLoadedRef.current = id
      } else {
        console.log('Datos ya cargados para el pedido:', id)
      }
    }
  }, [isEditing, order, loadingOrder, id, loadOrderData])

  // Inicializar estado de items para cada proveedor agregado
  useEffect(() => {
    if (formData.suppliers && formData.suppliers.length > 0) {
      const supplierIds = formData.suppliers.map(s => s?.supplierId).filter(Boolean)
      
      setItemStates(prev => {
        const newStates = { ...prev }
        let hasChanges = false
        
        supplierIds.forEach(supplierId => {
          if (!newStates[supplierId]) {
            newStates[supplierId] = {
              itemType: 'product',
              selectedProductId: '',
              selectedSupplyId: '',
              quantity: 1,
              price: '',
              suggestedPrice: null,
              size: '',
              color: '',
            }
            hasChanges = true
          }
        })
        
        Object.keys(newStates).forEach(key => {
          if (!supplierIds.includes(key)) {
            delete newStates[key]
            hasChanges = true
          }
        })
        
        return hasChanges ? newStates : prev
      })
    } else {
      setItemStates({})
    }
  }, [formData.suppliers])


  const calculateSuggestedPrice = async (productId, purchasePrice) => {
    if (!productId) {
      return null
    }

    try {
      const purchaseValue = Number(purchasePrice)
      if (!Number.isFinite(purchaseValue) || purchaseValue <= 0) {
        return null
      }
      const product = products.find(p => p.id === productId)
      if (!product || !product.categoryId) {
        return null
      }

      const category = categories.find(c => c.id === product.categoryId)
      const profitPercentage = await getCategoryProfitPercentage(category)
      const profitValue = Number(profitPercentage)
      if (!Number.isFinite(profitValue)) {
        return null
      }
      const suggested = purchaseValue * (1 + profitValue / 100)
      return Number.isFinite(suggested) ? suggested : null
    } catch (error) {
      console.error('Error calculando precio sugerido:', error)
      return null
    }
  }

  // Obtener estado de items para un proveedor (sin mutar)
  const getItemState = (supplierId) => {
    if (!itemStates[supplierId]) {
      // Retornar estado por defecto sin mutar
      return {
        itemType: 'product',
        selectedProductId: '',
        selectedSupplyId: '',
        quantity: 1,
        price: '',
        suggestedPrice: null,
        size: '',
        color: '',
      }
    }
    return itemStates[supplierId]
  }

  const updateItemState = (supplierId, updates) => {
    setItemStates(prev => {
      const currentState = prev[supplierId] || {
        itemType: 'product',
        selectedProductId: '',
        selectedSupplyId: '',
        quantity: 1,
        price: '',
        suggestedPrice: null,
        size: '',
        color: '',
      }
      return {
        ...prev,
        [supplierId]: {
          ...currentState,
          ...updates
        }
      }
    })
  }

  const handleAddSupplier = () => {
    if (!selectedSupplierId) {
      setNotification({
        type: 'error',
        message: 'Selecciona un proveedor'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    const supplier = suppliers.find(s => s.email === selectedSupplierId)
    if (!supplier) return

    // Verificar si ya existe
    if (formData.suppliers.some(s => s.supplierId === selectedSupplierId)) {
      setNotification({
        type: 'error',
        message: 'Este proveedor ya está agregado'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    setFormData(prev => ({
      ...prev,
      suppliers: [...prev.suppliers, {
        supplierId: selectedSupplierId,
        supplierName: supplier.name,
        items: [],
        subtotal: 0
      }]
    }))

    // Inicializar estado de items para este proveedor
    setItemStates(prev => {
      if (!prev[selectedSupplierId]) {
        return {
          ...prev,
          [selectedSupplierId]: {
            itemType: 'product',
            selectedProductId: '',
            selectedSupplyId: '',
            quantity: 1,
            price: '',
            suggestedPrice: null,
            size: '',
            color: '',
          }
        }
      }
      return prev
    })
    
    // Limpiar el select
    setSelectedSupplierId('')
  }

  const handleRemoveSupplier = (supplierId) => {
    setFormData(prev => ({
      ...prev,
      suppliers: prev.suppliers.filter(s => s.supplierId !== supplierId)
    }))
    // Limpiar estado de items de este proveedor
    setItemStates(prev => {
      const newStates = { ...prev }
      delete newStates[supplierId]
      return newStates
    })
  }

  const handleAddItem = (supplierId) => {
    const itemState = getItemState(supplierId)
    
    if (!itemState.quantity || !itemState.price) {
      setNotification({
        type: 'error',
        message: 'Completa todos los campos requeridos'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    const supplierIndex = formData.suppliers.findIndex(s => s.supplierId === supplierId)
    if (supplierIndex === -1) return

    let item = null

    if (itemState.itemType === 'product') {
      if (!itemState.selectedProductId) {
        setNotification({
          type: 'error',
          message: 'Selecciona un producto'
        })
        setTimeout(() => setNotification(null), 3000)
        return
      }

      const product = products.find(p => p.id === itemState.selectedProductId)
      if (!product) return
      
      const requiresColor = isFashion || (Array.isArray(product.colors) && product.colors.length > 0)
      const requiresSize = isFashion || (Array.isArray(product.sizes) && product.sizes.length > 0)
      if (requiresColor && !itemState.color) {
        setNotification({
          type: 'error',
          message: 'Selecciona un color para el producto'
        })
        setTimeout(() => setNotification(null), 3000)
        return
      }
      if (requiresSize && !itemState.size) {
        setNotification({
          type: 'error',
          message: 'Selecciona un talle para el producto'
        })
        setTimeout(() => setNotification(null), 3000)
        return
      }

      // Validar precio único por producto si se quiere actualizar
      if (itemState.updateProductPrice) {
        const validation = validateUniqueProductPriceUpdate(supplierId, product.id)
        if (!validation.valid) {
          setNotification({
            type: 'error',
            message: validation.message
          })
          setTimeout(() => setNotification(null), 5000)
          return
        }
      }

      // Validar que el precio final sea mayor al precio de compra (solo advertencia)
      const finalPrice = parseCurrencyInput(itemState.finalPrice || itemState.suggestedPrice || '0')
      const purchasePrice = parseCurrencyInput(itemState.price || '0')
      if (finalPrice < purchasePrice) {
        setNotification({
          type: 'error',
          message: `Advertencia: El precio de venta (${formatCurrency(finalPrice)}) es menor al precio de compra (${formatCurrency(purchasePrice)}). Esto generaría pérdidas.`
        })
        setTimeout(() => setNotification(null), 5000)
        // No retornar, solo mostrar advertencia
      }

      // Obtener el nombre de la categoría
      const category = categories.find(c => c.id === product.categoryId)
      const categoryName = category ? category.name : 'Sin categoría'

      item = {
        itemId: `item-${Date.now()}`,
        type: 'product',
        productId: product.id,
        name: product.name,
        sku: product.sku || '',
        categoryId: product.categoryId || null,
        category: categoryName, // Guardar el nombre de la categoría
        quantity: parseInt(itemState.quantity),
        unitPrice: parseCurrencyInput(itemState.price || '0'), // Precio de compra
        totalPrice: (Number.isFinite(Number(itemState.quantity)) ? Number(itemState.quantity) : 1) * parseCurrencyInput(itemState.price || '0'),
        suggestedPrice: itemState.suggestedPrice || null,
        finalPrice: finalPrice || null, // Precio final de venta
        updateProductPrice: itemState.updateProductPrice || false, // Checkbox
        currentProductPrice: itemState.currentProductPrice || product.price || null, // Precio actual del producto
        size: itemState.size || null,
        color: itemState.color || null
      }
    } else {
      if (!itemState.selectedSupplyId) {
        setNotification({
          type: 'error',
          message: 'Selecciona un insumo'
        })
        setTimeout(() => setNotification(null), 3000)
        return
      }

      const supply = supplies.find(s => s.id === itemState.selectedSupplyId)
      if (!supply) return

      item = {
        itemId: `item-${Date.now()}`,
        type: 'supply',
        supplyId: supply.id,
        name: supply.name,
        sku: supply.sku || '',
        categoryId: null,
        category: 'Insumos', // Los insumos tienen su propia categoría
        quantity: parseInt(itemState.quantity),
        unitPrice: parseCurrencyInput(itemState.price || '0'),
        totalPrice: (Number.isFinite(Number(itemState.quantity)) ? Number(itemState.quantity) : 1) * parseCurrencyInput(itemState.price || '0')
      }
    }

    const updatedSuppliers = [...formData.suppliers]
    updatedSuppliers[supplierIndex].items.push(item)
    updatedSuppliers[supplierIndex].subtotal = updatedSuppliers[supplierIndex].items.reduce(
      (sum, item) => sum + item.totalPrice, 0
    )

    setFormData(prev => ({
      ...prev,
      suppliers: updatedSuppliers
    }))

    // Limpiar campos de este proveedor
    updateItemState(supplierId, {
      selectedProductId: '',
      selectedSupplyId: '',
      quantity: 1,
      price: '',
      suggestedPrice: null,
      finalPrice: '',
      updateProductPrice: false,
      currentProductPrice: null,
      size: '',
      color: ''
    })
  }

  const handleRemoveItem = (supplierId, itemId) => {
    const suppliers = formData.suppliers.map(supplier => {
      if (supplier.supplierId === supplierId) {
        const items = supplier.items.filter(item => item.itemId !== itemId)
        return {
          ...supplier,
          items,
          subtotal: items.reduce((sum, item) => sum + item.totalPrice, 0)
        }
      }
      return supplier
    })

    setFormData(prev => ({
      ...prev,
      suppliers
    }))
  }

  const handleProductChange = async (supplierId, productId) => {
    // Obtener el estado actual
    const currentState = itemStates[supplierId] || {
      itemType: 'product',
      selectedProductId: '',
      selectedSupplyId: '',
      quantity: 1,
      price: '',
      suggestedPrice: null,
      finalPrice: '',
      updateProductPrice: false,
      currentProductPrice: null,
      size: '',
      color: ''
    }
    
    // Obtener el producto para obtener su precio actual
    const product = products.find(p => p.id === productId)
    const currentProductPrice = product?.price || null
    
    // Actualizar el estado con el producto seleccionado
    setItemStates(prev => ({
      ...prev,
      [supplierId]: {
        ...currentState,
        selectedProductId: productId,
        currentProductPrice,
        suggestedPrice: null, // Resetear precio sugerido, se calculará después si es necesario
        finalPrice: '', // Resetear precio final
        updateProductPrice: false, // Resetear checkbox
        size: '',
        color: ''
      }
    }))
    
    // Calcular precio sugerido si hay precio ingresado
    const priceValue = parseCurrencyInput(currentState.price || '0')
    if (priceValue > 0 && productId) {
      const suggested = await calculateSuggestedPrice(productId, priceValue)
      setItemStates(prev => ({
        ...prev,
        [supplierId]: {
          ...(prev[supplierId] || currentState),
          selectedProductId: productId,
          currentProductPrice,
          suggestedPrice: suggested,
          finalPrice: suggested ? suggested.toString() : '' // Inicializar con precio sugerido
        }
      }))
    }
  }

  const handlePriceChange = async (supplierId, value) => {
    const cleanedValue = String(value ?? '').replace(/[^0-9,.]/g, '');
    const priceStr = cleanedValue;
    const numericPrice = parseCurrencyInput(priceStr || '0')
    
    // Actualizar el estado con el precio usando el patrón funcional
    setItemStates(prev => {
      const currentState = prev[supplierId] || {
        itemType: 'product',
        selectedProductId: '',
        selectedSupplyId: '',
        quantity: 1,
        price: '',
        suggestedPrice: null
      }
      
      const updatedState = {
        ...currentState,
        price: priceStr
      }
      
      if (!priceStr) {
        updatedState.suggestedPrice = null
        updatedState.finalPrice = ''
        return {
          ...prev,
          [supplierId]: updatedState
        }
      }

      // Calcular precio sugerido si hay producto seleccionado
      if (updatedState.selectedProductId && numericPrice > 0) {
        calculateSuggestedPrice(updatedState.selectedProductId, numericPrice).then(suggested => {
          setItemStates(prevState => ({
            ...prevState,
            [supplierId]: {
              ...(prevState[supplierId] || currentState),
              price: priceStr,
              suggestedPrice: suggested,
              finalPrice: suggested ? suggested.toString() : (prevState[supplierId]?.finalPrice || '') // Mantener precio final si existe, sino usar sugerido
            }
          }))
        })
      } else {
        updatedState.suggestedPrice = null
      }
      
      return {
        ...prev,
        [supplierId]: updatedState
      }
    })
  }

  // Validar que solo un proveedor tenga configurado el precio para actualizar el mismo producto
  const validateUniqueProductPriceUpdate = (supplierId, productId) => {
    // Buscar si el mismo producto ya tiene updateProductPrice = true en otro proveedor
    for (const supplier of formData.suppliers) {
      if (supplier.supplierId !== supplierId) {
        const existingItem = supplier.items.find(item => 
          item.productId === productId && item.updateProductPrice === true
        )
        if (existingItem) {
          return {
            valid: false,
            message: `El producto "${existingItem.name}" ya tiene configurado actualizar precio en el proveedor "${supplier.supplierName}". Solo se puede configurar en un proveedor por orden.`
          }
        }
      }
    }
    return { valid: true }
  }

  // Manejar cambio en el checkbox de actualizar precio
  const handleUpdatePriceChange = (supplierId, checked) => {
    const itemState = getItemState(supplierId)
    
    // Si se marca el checkbox, validar que no haya otro proveedor con el mismo producto
    if (checked && itemState.selectedProductId) {
      const validation = validateUniqueProductPriceUpdate(supplierId, itemState.selectedProductId)
      if (!validation.valid) {
        setNotification({
          type: 'error',
          message: validation.message
        })
        setTimeout(() => setNotification(null), 5000)
        return // No actualizar el checkbox
      }
    }
    
    updateItemState(supplierId, {
      updateProductPrice: checked
    })
  }

  // Filtrar productos por proveedor
  const getProductsForSupplier = (supplierId) => {
    if (!supplierId) return []
    return products.filter(product => {
      const supplierIds = product.supplierIds || (product.supplierId ? [product.supplierId] : [])
      return supplierIds.includes(supplierId)
    })
  }

  // Filtrar insumos por proveedor
  const getSuppliesForSupplier = (supplierId) => {
    if (!supplierId) return []
    return supplies.filter(supply => {
      const supplierIds = supply.supplierIds || (supply.supplierId ? [supply.supplierId] : [])
      return supplierIds.includes(supplierId)
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (saving) return

    // Validación
    if (formData.suppliers.length === 0) {
      setNotification({
        type: 'error',
        message: 'Agrega al menos un proveedor con items'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    const hasItems = formData.suppliers.some(s => s.items.length > 0)
    if (!hasItems) {
      setNotification({
        type: 'error',
        message: 'Agrega al menos un item al pedido'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    // Guardar directamente sin actualizar precios
    // Los precios se actualizarán cuando se confirme la orden
    await saveOrder()
  }

  const saveOrder = async () => {
    try {
      const orderData = {
        suppliers: formData.suppliers,
        expectedDeliveryDate: formData.expectedDeliveryDate 
          ? Timestamp.fromDate(new Date(formData.expectedDeliveryDate))
          : null,
        taxes: parseCurrencyInput(formData.taxes || '0'),
        shipping: parseCurrencyInput(formData.shipping || '0'),
        notes: formData.notes || null,
        createdBy: currentUser?.uid || null,
        createdByName: userData?.name || userData?.displayName || 'Sistema'
      }

      if (isEditing) {
        await update(id, orderData)
        setNotification({
          type: 'success',
          message: 'Pedido actualizado correctamente'
        })
      } else {
        await create(orderData)
        setNotification({
          type: 'success',
          message: 'Pedido creado correctamente'
        })
      }

      // No actualizar precios aquí - se actualizarán al confirmar la orden
      setTimeout(() => {
        navigate('/admin/pedidos-compra')
      }, 1500)
    } catch (error) {
      console.error('Error guardando pedido:', error)
      setNotification({
        type: 'error',
        message: `Error al guardar: ${error.message || 'Error desconocido'}`
      })
      setTimeout(() => setNotification(null), 5000)
    }
  }

  const handleConfirmPriceUpdate = async () => {
    setShowPriceUpdateConfirm(false)
    await saveOrder()
  }

  const handleSummaryCurrencyChange = (field, value) => {
    let cleanedValue = value.replace(/[^0-9.,]/g, '')
    if (!cleanedValue) {
      setFormData(prev => ({ ...prev, [field]: '' }))
      return
    }
    cleanedValue = cleanedValue.replace('.', ',')
    const parts = cleanedValue.split(',')
    if (parts.length > 2) {
      cleanedValue = parts[0] + ',' + parts.slice(1).join('')
    }
    setFormData(prev => ({ ...prev, [field]: cleanedValue }))
  }

  const handleSummaryCurrencyFocus = (field) => {
    setEditingSummaryFields(prev => new Set(prev).add(field))
  }

  const handleSummaryCurrencyBlur = (field) => {
    const numericValue = parseCurrencyInput(formData[field] || '0')
    setFormData(prev => ({
      ...prev,
      [field]: numericValue > 0 ? numericValue.toString() : ''
    }))
    setEditingSummaryFields(prev => {
      const next = new Set(prev)
      next.delete(field)
      return next
    })
  }

  const getTotal = () => {
    const subtotal = formData.suppliers.reduce((sum, s) => sum + s.subtotal, 0)
    return subtotal + parseCurrencyInput(formData.taxes || '0') + parseCurrencyInput(formData.shipping || '0')
  }

  if (loadingOrder) {
    return (
      <div className="purchase-order-form-page">
        <div className="loading-state">
          <p>Cargando pedido...</p>
        </div>
      </div>
    )
  }

  // Si estamos editando y no hay pedido, mostrar error
  if (isEditing && !order && !loadingOrder) {
    return (
      <div className="purchase-order-form-page">
        <div className="loading-state">
          <p>Error: Pedido no encontrado</p>
          <Link to="/admin/pedidos-compra">
            <button className="back-button">← Volver a Pedidos de Compra</button>
          </Link>
        </div>
      </div>
    )
  }

  // Verificar que los datos necesarios estén cargados cuando estamos editando
  // Solo mostrar "Cargando" si tenemos el order pero aún no se han cargado los datos al formData
  // Verificamos si orderDataLoadedRef.current es diferente de id, lo que significa que aún no se han cargado los datos
  // Y también verificamos que formData.suppliers esté vacío (lo que indica que aún no se han cargado)
  if (isEditing && order && !loadingOrder && orderDataLoadedRef.current !== id && (!formData.suppliers || formData.suppliers.length === 0)) {
    // Esperar a que se carguen los datos - dar un pequeño delay para que el useEffect se ejecute
    console.log('Mostrando pantalla de carga - esperando datos del pedido')
    return (
      <div className="purchase-order-form-page">
        <div className="loading-state">
          <p>Cargando datos del pedido...</p>
        </div>
      </div>
    )
  }

  // Debug: Log del estado actual
  console.log('PurchaseOrderForm render:', {
    isEditing,
    loadingOrder,
    hasOrder: !!order,
    orderId: order?.id,
    loadedRef: orderDataLoadedRef.current,
    formDataSuppliers: formData.suppliers?.length || 0
  })

  return (
    <div className="purchase-order-form-page">
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />

      <div className="form-header">
        <div>
          <Link to="/admin/pedidos-compra">
            <button className="back-button">← Volver</button>
          </Link>
          <h1>{isEditing ? 'Editar Pedido de Compra' : 'Nuevo Pedido de Compra'}</h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="purchase-order-form">
        {/* Sección: Proveedores */}
        <div className="form-section">
          <h3>Proveedores</h3>
          
          <div className="add-supplier-section">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="currentSupplierId">Seleccionar Proveedor</label>
                <select
                  id="currentSupplierId"
                  value={selectedSupplierId}
                  onChange={(e) => setSelectedSupplierId(e.target.value)}
                  disabled={saving}
                  className="form-input"
                >
                  <option value="">Seleccionar...</option>
                  {suppliers
                    .filter(s => !formData.suppliers.some(fs => fs.supplierId === s.email))
                    .map(supplier => (
                      <option key={supplier.email} value={supplier.email}>
                        {supplier.name} {supplier.businessName ? `(${supplier.businessName})` : ''}
                      </option>
                    ))}
                </select>
              </div>
              <div className="form-group">
                <label style={{ visibility: 'hidden', height: '1.5rem' }}>Espacio</label>
                <Button
                  type="button"
                  onClick={handleAddSupplier}
                  disabled={saving || !canEdit || !selectedSupplierId}
                  variant="outline"
                  style={{ height: 'calc(0.75rem * 2 + 1rem + 2px)', alignSelf: 'stretch' }}
                >
                  ➕ Agregar Proveedor
                </Button>
              </div>
            </div>
          </div>

          {/* Lista de proveedores agregados */}
          {formData.suppliers.map((supplier, _supplierIndex) => (
            <div key={supplier.supplierId} className="supplier-section">
              <div className="supplier-header">
                <h4>{supplier.supplierName}</h4>
                <Button
                  type="button"
                  variant="danger"
                  size="small"
                  onClick={() => handleRemoveSupplier(supplier.supplierId)}
                  disabled={saving}
                >
                  ✕ Remover
                </Button>
              </div>

              {/* Agregar items a este proveedor */}
              <div className="add-item-section">
                <div className="form-row">
                  {(() => {
                    const itemState = getItemState(supplier.supplierId)
                    const supplierProducts = getProductsForSupplier(supplier.supplierId)
                    const supplierSupplies = getSuppliesForSupplier(supplier.supplierId)
                    
                    return (
                      <>
                        <div className="form-group">
                          <label>Tipo</label>
                          <select
                            value={itemState.itemType || 'product'}
                            onChange={(e) => {
                              updateItemState(supplier.supplierId, {
                                itemType: e.target.value,
                                selectedProductId: '',
                                selectedSupplyId: '',
                                suggestedPrice: null,
                                size: '',
                                color: ''
                              })
                            }}
                            disabled={saving || !canEdit}
                            className="form-input"
                          >
                            <option value="product">Producto</option>
                            <option value="supply">Insumo</option>
                          </select>
                        </div>

                        {itemState.itemType === 'product' ? (
                          <div className="form-group">
                            <label>Producto</label>
                            <select
                              value={itemState.selectedProductId || ''}
                              onChange={(e) => handleProductChange(supplier.supplierId, e.target.value)}
                              disabled={saving || !canEdit}
                              className="form-input"
                            >
                              <option value="">Seleccionar producto...</option>
                              {supplierProducts.length === 0 ? (
                                <option value="" disabled>
                                  No hay productos para este proveedor
                                </option>
                              ) : (
                                supplierProducts.map(product => (
                                  <option key={product.id} value={product.id}>
                                    {product.name} {product.sku ? `(${product.sku})` : ''}
                                  </option>
                                ))
                              )}
                            </select>
                            {supplierProducts.length === 0 && (
                              <small className="form-hint">
                                Este proveedor no tiene productos asociados. Asigna productos al proveedor desde la gestión de productos.
                              </small>
                            )}
                          </div>
                        ) : (
                          <div className="form-group">
                            <label>Insumo</label>
                            <select
                              value={itemState.selectedSupplyId}
                              onChange={(e) => updateItemState(supplier.supplierId, { selectedSupplyId: e.target.value })}
                              disabled={saving || !canEdit}
                              className="form-input"
                            >
                              <option value="">Seleccionar insumo...</option>
                              {supplierSupplies.length === 0 ? (
                                <option value="" disabled>
                                  No hay insumos para este proveedor
                                </option>
                              ) : (
                                supplierSupplies.map(supply => (
                                  <option key={supply.id} value={supply.id}>
                                    {supply.name} {supply.sku ? `(${supply.sku})` : ''}
                                  </option>
                                ))
                              )}
                            </select>
                            {supplierSupplies.length === 0 && (
                              <small className="form-hint">
                                Este proveedor no tiene insumos asociados. Asigna insumos al proveedor desde la gestión de insumos.
                              </small>
                            )}
                          </div>
                        )}
                        
                        {itemState.itemType === 'product' && itemState.selectedProductId && (() => {
                          const selectedProduct = supplierProducts.find(p => p.id === itemState.selectedProductId)
                          const requiresColor = isFashion || (Array.isArray(selectedProduct?.colors) && selectedProduct.colors.length > 0)
                          const requiresSize = isFashion || (Array.isArray(selectedProduct?.sizes) && selectedProduct.sizes.length > 0)
                          return (
                            <>
                              {requiresColor && (
                                <div className="form-group">
                                  <label>Color</label>
                                  <select
                                    value={itemState.color || ''}
                                    onChange={(e) => updateItemState(supplier.supplierId, { color: e.target.value })}
                                    disabled={saving || !canEdit}
                                    className="form-input"
                                  >
                                    <option value="">Seleccionar color...</option>
                                    {(selectedProduct?.colors || []).length === 0 ? (
                                      <option value="" disabled>
                                        No hay colores definidos para este producto
                                      </option>
                                    ) : (
                                      (selectedProduct?.colors || []).map((c) => (
                                        <option key={c} value={c}>{getColorLabel(selectedProduct, c)}</option>
                                      ))
                                    )}
                                  </select>
                                </div>
                              )}
                              {requiresSize && (
                                <div className="form-group">
                                  <label>Talle</label>
                                  <select
                                    value={itemState.size || ''}
                                    onChange={(e) => updateItemState(supplier.supplierId, { size: e.target.value })}
                                    disabled={saving || !canEdit}
                                    className="form-input"
                                  >
                                    <option value="">Seleccionar talle...</option>
                                    {(selectedProduct?.sizes || []).map((s) => (
                                      <option key={s} value={s}>{s}</option>
                                    ))}
                                  </select>
                                </div>
                              )}
                            </>
                          )
                        })()}

                        <div className="form-group">
                          <label>Cantidad</label>
                          <input
                            type="number"
                            value={itemState.quantity}
                            onChange={(e) => updateItemState(supplier.supplierId, { quantity: e.target.value })}
                            min="1"
                            disabled={saving || !canEdit}
                            className="form-input"
                          />
                        </div>

                        <div className="form-group price-purchase-group">
                          <label>Precio de Compra (por unidad)</label>
                          <input
                            type="text"
                                value={
                                  editingFields[supplier.supplierId]?.has('price')
                                    ? (itemState.price ? `$${itemState.price.replace('.', ',')}` : '')
                                    : (itemState.price ? formatCurrencyInput(itemState.price, true) : '')
                                }
                            onChange={(e) => {
                              let cleanedValue = e.target.value.replace(/[^0-9.,]/g, '')
                              if (!cleanedValue) {
                                handlePriceChange(supplier.supplierId, '')
                                return
                              }
                              cleanedValue = cleanedValue.replace('.', ',')
                              const parts = cleanedValue.split(',')
                              if (parts.length > 2) {
                                cleanedValue = parts[0] + ',' + parts.slice(1).join('')
                              }
                              handlePriceChange(supplier.supplierId, cleanedValue)
                            }}
                            onFocus={() => {
                              setEditingFields(prev => ({
                                ...prev,
                                [supplier.supplierId]: new Set(prev[supplier.supplierId] || []).add('price')
                              }))
                            }}
                            onBlur={() => {
                              if (!itemState.price) {
                                handlePriceChange(supplier.supplierId, '')
                              } else {
                                handlePriceChange(supplier.supplierId, itemState.price)
                              }
                              setEditingFields(prev => {
                                const newSet = { ...prev }
                                if (newSet[supplier.supplierId]) {
                                  newSet[supplier.supplierId] = new Set(newSet[supplier.supplierId])
                                  newSet[supplier.supplierId].delete('price')
                                }
                                return newSet
                              })
                            }}
                            disabled={saving || !canEdit}
                            className="form-input"
                            placeholder="$0"
                          />
                        </div>
                        
                        {(() => {
                          const quantityRaw = Number.parseFloat(itemState.quantity)
                          const priceRaw = parseCurrencyInput(itemState.price || '0')
                          const quantityValue = Number.isFinite(quantityRaw) ? quantityRaw : 0
                          const priceValue = Number.isFinite(priceRaw) ? priceRaw : 0
                          const totalPurchase = quantityValue * priceValue
                          const showTotalPurchase = quantityValue > 0 && priceValue > 0
                          const suggestedValue = Number.isFinite(itemState.suggestedPrice)
                            ? itemState.suggestedPrice
                            : null
                          const currentPriceRaw = itemState.currentProductPrice
                          const currentPriceParsed = typeof currentPriceRaw === 'number'
                            ? currentPriceRaw
                            : (currentPriceRaw != null ? parseCurrencyInput(String(currentPriceRaw)) : NaN)
                          const currentPriceValue = Number.isFinite(currentPriceParsed) ? currentPriceParsed : null
                          const showSuggestedInfo = suggestedValue !== null && itemState.itemType === 'product'

                          if (!showTotalPurchase && !showSuggestedInfo) {
                            return null
                          }

                          return (
                            <div className="price-info-container">
                              {showTotalPurchase && (
                                <div className="price-info-item total-purchase-highlight">
                                  <span className="price-info-icon">🧮</span>
                                  <span className="price-info-label">Total compra:</span>
                                  <span className="price-info-value">{formatCurrency(totalPurchase)}</span>
                                </div>
                              )}
                              {showSuggestedInfo && (
                                <>
                                  <div className="price-info-item suggested-price-highlight">
                                    <span className="price-info-icon">💡</span>
                                    <span className="price-info-label">Precio de venta sugerido:</span>
                                    <span className="price-info-value">{formatCurrency(suggestedValue)}</span>
                                  </div>
                                  {currentPriceValue !== null && (
                                    <div className="price-info-item current-price-highlight">
                                      <span className="price-info-icon">📊</span>
                                      <span className="price-info-label">Precio actual del producto:</span>
                                      <span className="price-info-value">{formatCurrency(currentPriceValue || 0)}</span>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          )
                        })()}

                        {itemState.itemType === 'product' && itemState.selectedProductId && Number.isFinite(itemState.suggestedPrice) && (
                          <>
                            <div className="form-group">
                              <label>
                                Precio de Venta Final
                                <span className="required">*</span>
                              </label>
                              <input
                                type="text"
                                value={
                                  editingFields[supplier.supplierId]?.has('finalPrice')
                                    ? (itemState.finalPrice ? `$${itemState.finalPrice.replace('.', ',')}` : '')
                                    : (itemState.finalPrice ? formatCurrencyInput(itemState.finalPrice, true) : '')
                                }
                                onChange={(e) => {
                                  let cleanedValue = e.target.value.replace(/[^0-9.,]/g, '')
                                  if (!cleanedValue) {
                                    updateItemState(supplier.supplierId, { finalPrice: '' })
                                    return
                                  }
                                  cleanedValue = cleanedValue.replace('.', ',')
                                  const parts = cleanedValue.split(',')
                                  if (parts.length > 2) {
                                    cleanedValue = parts[0] + ',' + parts.slice(1).join('')
                                  }
                                  const finalPriceNum = parseCurrencyInput(cleanedValue)
                                  const finalPriceStr = finalPriceNum > 0 ? finalPriceNum.toString() : ''
                                  updateItemState(supplier.supplierId, { finalPrice: finalPriceStr })
                                  
                                  // Validar y mostrar advertencia si es menor al precio de compra
                                  const purchasePrice = parseFloat(itemState.price || 0)
                                  if (finalPriceNum > 0 && finalPriceNum < purchasePrice) {
                                    setNotification({
                                      type: 'error',
                                      message: `Advertencia: El precio de venta (${formatCurrency(finalPriceNum)}) es menor al precio de compra (${formatCurrency(purchasePrice)}). Esto generaría pérdidas.`
                                    })
                                    setTimeout(() => setNotification(null), 5000)
                                  }
                                  
                                  // Mostrar alerta si el cambio es > 20%
                                  if (itemState.currentProductPrice && finalPriceNum > 0) {
                                    const changePercent = Math.abs((finalPriceNum - itemState.currentProductPrice) / itemState.currentProductPrice * 100)
                                    if (changePercent > 20) {
                                      setNotification({
                                        type: 'error',
                                        message: `⚠️ Cambio significativo de precio: ${changePercent.toFixed(1)}% (${formatCurrency(itemState.currentProductPrice)} → ${formatCurrency(finalPriceNum)})`
                                      })
                                      setTimeout(() => setNotification(null), 5000)
                                    }
                                  }
                                }}
                                onFocus={() => {
                                  setEditingFields(prev => ({
                                    ...prev,
                                    [supplier.supplierId]: new Set(prev[supplier.supplierId] || []).add('finalPrice')
                                  }))
                                }}
                                onBlur={() => {
                                  const finalPriceNum = parseCurrencyInput(itemState.finalPrice || '0')
                                  const finalPriceStr = finalPriceNum > 0 ? finalPriceNum.toString() : ''
                                  updateItemState(supplier.supplierId, { finalPrice: finalPriceStr })
                                  setEditingFields(prev => {
                                    const newSet = { ...prev }
                                    if (newSet[supplier.supplierId]) {
                                      newSet[supplier.supplierId] = new Set(newSet[supplier.supplierId])
                                      newSet[supplier.supplierId].delete('finalPrice')
                                    }
                                    return newSet
                                  })
                                }}
                                disabled={saving || !canEdit}
                                className="form-input"
                                placeholder={itemState.suggestedPrice ? formatCurrency(itemState.suggestedPrice) : "$0"}
                                required
                              />
                            </div>
                            <div className="form-group checkbox-group">
                              <label className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={itemState.updateProductPrice || false}
                                  onChange={(e) => handleUpdatePriceChange(supplier.supplierId, e.target.checked)}
                                  disabled={saving || !canEdit || !itemState.finalPrice}
                                />
                                <span>Actualizar precio del producto al guardar la orden</span>
                              </label>
                            </div>
                          </>
                        )}

                        <Button
                          type="button"
                          onClick={() => handleAddItem(supplier.supplierId)}
                          disabled={saving || !itemState.quantity || !itemState.price || 
                            (itemState.itemType === 'product' && !itemState.selectedProductId) ||
                            (itemState.itemType === 'supply' && !itemState.selectedSupplyId) ||
                            (itemState.itemType === 'product' && (() => {
                              const selectedProduct = supplierProducts.find(p => p.id === itemState.selectedProductId)
                              const requiresColor = isFashion || (Array.isArray(selectedProduct?.colors) && selectedProduct.colors.length > 0)
                              const requiresSize = isFashion || (Array.isArray(selectedProduct?.sizes) && selectedProduct.sizes.length > 0)
                              return (requiresColor && !itemState.color) || (requiresSize && !itemState.size)
                            })())}
                          variant="outline"
                        >
                          ➕ Agregar
                        </Button>
                      </>
                    )
                  })()}
                </div>
              </div>

              {/* Items del proveedor */}
              {supplier.items.length > 0 && (
                <div className="items-list">
                  <table className="items-table">
                    <thead>
                      <tr>
                        <th>Tipo</th>
                        <th>Nombre</th>
                        <th>Variante</th>
                        <th>SKU</th>
                        <th>Cantidad</th>
                        <th>Precio Unit.</th>
                        {supplier.items.some(item => item.type === 'product') && (
                          <th>Precio Producto</th>
                        )}
                        <th>Total</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {supplier.items.map((item) => (
                        <tr key={item.itemId}>
                          <td>{item.type === 'product' ? '📦 Producto' : '🔧 Insumo'}</td>
                          <td>{item.name}</td>
                          <td>{item.type === 'product' ? `${item.size || '-'} / ${item.color || '-'}` : '-'}</td>
                          <td>{item.sku}</td>
                          <td>{item.quantity}</td>
                          <td>{formatCurrency(item.unitPrice)}</td>
                          {supplier.items.some(i => i.type === 'product') && (
                            <td className="price-info-cell">
                              {item.type === 'product' ? (
                                item.updateProductPrice && item.finalPrice ? (
                                  <div className="price-update-info">
                                    <span className="price-update-badge">🔄 Actualizar</span>
                                    <span className="price-update-value">
                                      -Nuevo: {formatCurrency(parseFloat(item.finalPrice))}
                                    </span>
                                    {item.currentProductPrice && (
                                      <span className="price-update-current">
                                        {" "}(Actual: {formatCurrency(item.currentProductPrice)})
                                      </span>
                                    )}
                                  </div>
                                ) : item.currentProductPrice ? (
                                  <div className="price-no-update-info">
                                    <span className="price-no-update-badge">📊 Sin cambios</span>
                                    <span className="price-no-update-value">
                                      -Actual: {formatCurrency(item.currentProductPrice)}
                                    </span>
                                  </div>
                                ) : (
                                  <span className="price-unknown">-</span>
                                )
                              ) : (
                                <span className="price-not-applicable">-</span>
                              )}
                            </td>
                          )}
                          <td><strong>{formatCurrency(item.totalPrice)}</strong></td>
                          <td>
                            <Button
                              type="button"
                              variant="danger"
                              size="small"
                              onClick={() => handleRemoveItem(supplier.supplierId, item.itemId)}
                              disabled={saving || !canEdit}
                            >
                              ✕
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="5" className="text-right"><strong>Subtotal:</strong></td>
                        <td><strong>{formatCurrency(supplier.subtotal)}</strong></td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Sección: Información General */}
        <div className="form-section">
          <h3>Información General</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="expectedDeliveryDate">Fecha de Entrega Esperada</label>
              <input
                type="date"
                id="expectedDeliveryDate"
                value={formData.expectedDeliveryDate}
                onChange={(e) => setFormData(prev => ({ ...prev, expectedDeliveryDate: e.target.value }))}
                disabled={saving}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="taxes">Impuestos</label>
              <input
                type="text"
                id="taxes"
                value={
                  editingSummaryFields.has('taxes')
                    ? (formData.taxes ? `$${String(formData.taxes).replace('.', ',')}` : '')
                    : (formData.taxes ? formatCurrencyInput(formData.taxes, true) : '')
                }
                onChange={(e) => handleSummaryCurrencyChange('taxes', e.target.value)}
                onFocus={() => handleSummaryCurrencyFocus('taxes')}
                onBlur={() => handleSummaryCurrencyBlur('taxes')}
                disabled={saving}
                className="form-input"
                placeholder="$0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="shipping">Envío</label>
              <input
                type="text"
                id="shipping"
                value={
                  editingSummaryFields.has('shipping')
                    ? (formData.shipping ? `$${String(formData.shipping).replace('.', ',')}` : '')
                    : (formData.shipping ? formatCurrencyInput(formData.shipping, true) : '')
                }
                onChange={(e) => handleSummaryCurrencyChange('shipping', e.target.value)}
                onFocus={() => handleSummaryCurrencyFocus('shipping')}
                onBlur={() => handleSummaryCurrencyBlur('shipping')}
                disabled={saving}
                className="form-input"
                placeholder="$0"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="notes">Notas</label>
            <textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows="3"
              disabled={saving}
              className="form-input"
            />
          </div>
        </div>

        {/* Resumen */}
        <div className="form-section summary-section">
          <h3>Resumen del Pedido</h3>
          <div className="summary-grid">
            <div className="summary-item">
              <span>Subtotal:</span>
              <span>{formatCurrency(formData.suppliers.reduce((sum, s) => sum + s.subtotal, 0))}</span>
            </div>
            <div className="summary-item">
              <span>Impuestos:</span>
              <span>{formatCurrency(parseCurrencyInput(formData.taxes || '0'))}</span>
            </div>
            <div className="summary-item">
              <span>Envío:</span>
              <span>{formatCurrency(parseCurrencyInput(formData.shipping || '0'))}</span>
            </div>
            <div className="summary-item summary-total">
              <span>Total:</span>
              <span>{formatCurrency(getTotal())}</span>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/admin/pedidos-compra')}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={saving || !canEdit}
          >
            {saving ? 'Guardando...' : (isEditing ? 'Actualizar Pedido' : 'Crear Pedido')}
          </Button>
        </div>
      </form>

      {/* Dialog: Confirmar actualización de precios */}
      <ConfirmDialog
        isOpen={showPriceUpdateConfirm}
        onClose={() => {
          setShowPriceUpdateConfirm(false)
          setPendingPriceUpdates([])
        }}
        onConfirm={handleConfirmPriceUpdate}
        title="Confirmar Actualización de Precios"
        message={
          <div className="price-update-confirm">
            <p>Se actualizarán los precios de los siguientes productos:</p>
            <table className="price-update-table">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Precio Actual</th>
                  <th>Nuevo Precio</th>
                  <th>Cambio</th>
                </tr>
              </thead>
              <tbody>
                {pendingPriceUpdates.map((update, index) => {
                  const change = update.newPrice - update.currentPrice
                  const changePercent = update.currentPrice > 0 
                    ? ((change / update.currentPrice) * 100).toFixed(1)
                    : '0'
                  const isSignificant = Math.abs(changePercent) > 20
                  
                  return (
                    <tr key={index} className={isSignificant ? 'significant-change' : ''}>
                      <td>{update.productName}</td>
                      <td>{formatCurrency(update.currentPrice)}</td>
                      <td>{formatCurrency(update.newPrice)}</td>
                      <td className={change >= 0 ? 'positive' : 'negative'}>
                        {change >= 0 ? '+' : ''}{formatCurrency(change)} ({changePercent}%)
                        {isSignificant && <span className="warning-badge">⚠️</span>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <p className="confirm-warning">
              ⚠️ Esta acción actualizará los precios de los productos y se registrará en el historial. 
              Los precios de las variantes también se actualizarán.
            </p>
          </div>
        }
        confirmText="Confirmar y Guardar"
        cancelText="Cancelar"
        variant="warning"
      />
    </div>
  )
}

export default PurchaseOrderForm

